// Step 3: Sections editor. Renders a list of SectionEditor cards. The user
// can add unlimited sections and remove any. Every measurement is per-
// section and eave-to-ridge driven.
import React from "react";
import { Plus, Sparkle } from "@phosphor-icons/react";
import SectionEditor from "./SectionEditor";
import { newBlankSection } from "../../lib/roofPresets";
import RoofPlanSVG from "../RoofPlanSVG";

export default function SectionsManager({ sections, perSection, warnings, onChange, onBack, onNext }) {
  const setAt = (idx, next) => onChange(sections.map((s, i) => (i === idx ? next : s)));
  const removeAt = (idx) => onChange(sections.filter((_, i) => i !== idx));
  const add = () => onChange([...sections, newBlankSection(`Section ${sections.length + 1}`)]);

  const totalArea = (perSection || []).reduce((a, s) => a + (s.roof_area_m2 || 0), 0);

  return (
    <div className="card-elev p-5" data-testid="step-measurements">
      <div className="overline">Step 3</div>
      <div className="flex items-end justify-between flex-wrap gap-3 mt-1">
        <div>
          <h3 className="font-display text-2xl font-bold">Sections &amp; measurements</h3>
          <p className="text-sm text-slate-500 mt-1">
            Every measurement is <strong>eave-to-ridge</strong> (slope length), not roof width. Add unlimited sections to model any geometry — dormers, valleys, hips, abutments, Velux, solar PV…
          </p>
        </div>
        <div className="text-right font-mono-data">
          <div className="text-xs text-slate-500 uppercase tracking-wider">Total area</div>
          <div className="font-bold text-2xl text-[var(--rqp-orange)]">{totalArea.toFixed(2)} m²</div>
          <div className="text-[10px] text-slate-500">{sections.length} section{sections.length === 1 ? "" : "s"}</div>
        </div>
      </div>

      <div className="mt-5">
        <RoofPlanSVG sections={sections} perSection={perSection} />
      </div>

      <div className="mt-5 space-y-3">
        {sections.map((s, i) => (
          <SectionEditor
            key={s.id || i}
            index={i}
            section={s}
            perSectionCalc={(perSection || [])[i]}
            sectionWarnings={(warnings || []).filter((w) => w.section_index === i)}
            onChange={(next) => setAt(i, next)}
            onRemove={() => removeAt(i)}
          />
        ))}
        {sections.length === 0 && (
          <div className="border-2 border-dashed border-slate-300 p-8 text-center rounded-sm">
            <p className="text-slate-500 text-sm">No sections yet. Pick a preset in Step 2 or add a blank section below.</p>
          </div>
        )}
      </div>

      <div className="mt-4 flex items-center gap-2">
        <button onClick={add} className="btn-secondary flex items-center gap-2" data-testid="add-section">
          <Plus size={12} weight="bold" /> Add section
        </button>
        <button
          disabled
          className="btn-secondary flex items-center gap-2 opacity-50 cursor-not-allowed"
          title="Phase 3 — coming soon. Auto-measure roofs from postcode + satellite imagery."
          data-testid="ai-measure-coming"
        >
          <Sparkle size={12} weight="fill" /> AI auto-measure <span className="text-[10px] uppercase tracking-wider opacity-70">soon</span>
        </button>
      </div>

      <div className="mt-5 flex justify-between">
        <button className="btn-secondary" onClick={onBack}>← Back</button>
        <button className="btn-primary" onClick={onNext} data-testid="step-3-next" disabled={sections.length === 0}>
          Continue →
        </button>
      </div>
    </div>
  );
}
